import page from 'page';
import { session } from '../session';
import { app } from '../app';
import i18n from '../i18n';
import { requestWithStatus, requestRawWithStatus, requestWithStatusRetry } from './base_request';

export interface ValidationResult<T extends object = {}> {
  isValid: boolean;
  entityId: string;
  errors: any;
  originalResponse?: T;
}

export interface DependentRecord {
  entity_type: string;
  entities: string[];
}

export interface RemoveResult {
  did_delete: boolean;
  dependant_records: DependentRecord[];
}

export interface ListRequestParams {
  offset?: number;
  limit?: number;
  name_prefix?: string;
  role?: 'viewer' | 'editor' | 'head';
  search?: string;
}
export interface UserListRequestParams {
  offset?: number;
  limit?: number;
  name_prefix?: string;
  country?: string;
  role?: 'viewer' | 'editor' | 'head';
}

/**
 * Rejection marker for a 404 on a request made with `silent404`. Lets the caller show its
 * own message instead of the generic entity-not-found modal, while every other status keeps
 * its shared handling.
 */
export const NOT_FOUND = Symbol('not-found');

function onErrorRetry<T>(req: () => Promise<T>): Promise<T> {
  return req().catch((obs: (cb: (retry: boolean) => void) => void) => {
    if ((obs as unknown) === NOT_FOUND) {
      return Promise.reject(NOT_FOUND);
    }
    if (!obs) {
      return Promise.reject(null);
    }

    return new Promise((accept, reject) => {
      let re = (obs: (cb: (retry: boolean) => void) => void) => {
        if (!obs) {
          return Promise.reject(null);
        }

        return new Promise(() => {
          obs((retry) => {
            if (retry) {
              req().then(accept, re);
            } else {
              reject(new Error('request'));
            }
          });
        });
      };

      obs((retry) => {
        if (retry) {
          req().then(accept, re);
        } else {
          reject(new Error('request'));
        }
      });
    });
  });
}

/**
 * Pulls user-displayable messages out of a DRF error body. Recognised shapes:
 *   - `{ detail: "message" }` — auth/permission/non-field errors
 *   - `{ field: ["message", ...] }` — serializer-level validators
 * Other shapes (bare string values, nested dicts, per-item arrays) are skipped
 * on purpose: those either don't follow DRF conventions or are already bound
 * to a specific field by the form layer.
 */
export function extractServerErrorMessages(serverErrors: unknown): string[] {
  if (!serverErrors || typeof serverErrors !== 'object') {
    return [];
  }
  const errors = serverErrors as Record<string, unknown>;
  if (typeof errors.detail === 'string') {
    return [errors.detail];
  }
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every((item) => typeof item === 'string')
    ) {
      messages.push(...(value as string[]));
    }
  }
  return messages;
}

export function handleErrorResponse(
  res: { status: number; result: {} },
  options?: { silent403?: boolean; silent404?: boolean }
) {
  if (res.status == 401) {
    if (res.result && (<any>res.result).detail === 'expired') {
      page.redirect(session.toTenantPath('/subscription/'));
    } else {
      app.showLoggedOut(true);
    }
  } else if (res.status == 404) {
    if (options?.silent404) {
      return Promise.reject(NOT_FOUND);
    }
    app.showEntityNotFound(true);
  } else if (res.status == 403) {
    if (!options?.silent403) {
      const detail = extractServerErrorMessages(res.result)[0];
      // reload: false — reloading after a load-time 403 would just trigger it again.
      app.showFormSubmissionErrorModal(
        detail || i18n.t('You do not have permission to perform this action.')(),
        { reload: false }
      );
    }
  } else {
    return Promise.reject(app.showServerError);
  }

  return Promise.reject(null);
}

export interface RequestOptions {
  injectTenant: boolean;
  skipToken?: boolean;
  version?: number;
  skipErrorResponseHandling?: boolean;
  silent403?: boolean;
  silent404?: boolean;
  onHeadersReceived?: (headers: string) => void;
}

export function request<ResultType>(
  method: string,
  path: string,
  params?: {},
  options?: RequestOptions,
  timeout?: number
): Promise<ResultType> {
  if (!options || options.injectTenant) {
    path = injectTenantInURL(path, options?.version ?? 1);
  }
  function handleResponse(res: any, options: RequestOptions) {
    if (res.headers && options?.onHeadersReceived) {
      options.onHeadersReceived(res.headers);
    }
    if (res.status == 200 || res.status == 201 || res.status == 202 || res.status == 204) {
      return res.result;
    } else {
      if (options?.skipErrorResponseHandling) {
        return res.result;
      } else {
        return handleErrorResponse(res, options);
      }
    }
  }
  return onErrorRetry(() => {
    if (options?.skipToken) {
      return requestWithStatus<ResultType>(method, path, undefined, params, timeout).then(
        (res) => {
          return handleResponse(res, options);
        },
        () => Promise.reject(app.showNetworkError)
      );
    } else {
      return session
        .getToken()
        .then((token) => requestWithStatus<ResultType>(method, path, token, params, timeout))
        .then(
          (res) => {
            return handleResponse(res, options);
          },
          () => Promise.reject(app.showNetworkError)
        );
    }
  });
}

export function requestBackgroundRetry<ResultType>(
  method: string,
  path: string,
  params?: {},
  options?: { injectTenant: boolean },
  timeout?: number
): Promise<ResultType> {
  if (!options || options.injectTenant) {
    path = injectTenantInURL(path);
  }

  return onErrorRetry(() =>
    session
      .getToken()
      .then((token) => requestWithStatusRetry<ResultType>(method, path, token, params, timeout))
      .then(
        (res) => {
          if (res.status == 200 || res.status == 201) {
            return res.result;
          } else {
            return handleErrorResponse(res);
          }
        },
        () => Promise.reject(app.showNetworkError)
      )
  );
}

export function requestRaw(
  method: string,
  path: string,
  params?: {},
  options?: { injectTenant: boolean; version?: number }
): Promise<Blob> {
  if (!options || options.injectTenant) {
    path = injectTenantInURL(path, options?.version ?? 1);
  }

  return onErrorRetry(() =>
    session
      .getToken()
      .then((token) => requestRawWithStatus(method, path, token, params))
      .then(
        (res) => {
          if (res.status == 200) {
            return res.data;
          } else {
            return handleErrorResponse({ status: res.status, result: {} });
          }
        },
        () => Promise.reject(app.showNetworkError)
      )
  );
}

/**
 * Make a request and return a ValidationResult object.
 * @param {str} method : HTTP method
 * @param {str} path : URL path
 * @param {object} params : Request body
 * @template {object} T : type of the original response. If provided, the original
 * response will be returned in the `originalResponse` field of the result.
 * @returns : A promise that resolves to a ValidationResult object.
 */
export function requestWithValidation<T extends object = {}>(
  method: string,
  path: string,
  params?: {},
  version?: number
): Promise<ValidationResult<T>> {
  return requestAnyWithValidation(method, path, params, version).then((val): ValidationResult<T> => {
    if (val.status == 200 || val.status == 201) {
      return { isValid: true, entityId: val.result.id, errors: {}, originalResponse: val.result as T };
    } else {
      return { isValid: false, entityId: null, errors: val.result };
    }
  });
}

export function requestAnyWithValidation(
  method: string,
  path: string,
  params?: {},
  version?: number
): Promise<{ status: number; result: any }> {
  return onErrorRetry(() =>
    session
      .getToken()
      .then((token) => requestWithStatus<any>(method, injectTenantInURL(path, version), token, params))
      .then(
        (val) => {
          if (val.status == 200 || val.status == 201 || val.status == 400) {
            return val;
          }

          return handleErrorResponse(val);
        },
        () => Promise.reject(app.showNetworkError)
      )
  );
}

function handleRemoveResponse(response: { status: number; result: any }): RemoveResult | Promise<never> {
  if (response.status == 200 || response.status == 204) {
    return { did_delete: true, dependant_records: [] };
  }

  if (response.status == 400 && Array.isArray(response.result)) {
    return { did_delete: false, dependant_records: response.result as DependentRecord[] };
  }

  // A 400 that is not a dependency list carries its own explanation, which the dependency
  // renderer would show as an empty list of records to remove first.
  const [serverMessage] = extractServerErrorMessages(response.result);
  if (response.status == 400 && serverMessage) {
    app.showFormSubmissionErrorModal(serverMessage, { reload: false });
    return Promise.reject(null);
  }

  return handleErrorResponse(response);
}

export function requestRemoveEntity(
  path: string,
  options?: {
    version?: number;
    params?: {};
  }
): Promise<RemoveResult> {
  return onErrorRetry(() =>
    session
      .getToken()
      .then((token) =>
        requestWithStatus('DELETE', injectTenantInURL(path, options?.version), token, options?.params)
      )
      .then(handleRemoveResponse, () => Promise.reject(app.showNetworkError))
  );
}

export function requestBulkRemoveEntities(
  path: string,
  options?: {
    version?: number;
  }
): Promise<RemoveResult> {
  return onErrorRetry(() =>
    session
      .getToken()
      .then((token) => requestWithStatus('DELETE', injectTenantInURL(path, options?.version), token))
      .then(handleRemoveResponse, () => Promise.reject(app.showNetworkError))
  );
}

export function listRequest<Data>(
  baseURL: string,
  params: ListRequestParams,
  options?: RequestOptions
): Promise<Data[]> {
  return requestWithGetParams(baseURL, params, options);
}

export function requestWithGetParams<Data>(
  baseURL: string,
  params: {},
  options?: RequestOptions
): Promise<Data> {
  let url = baseURL.indexOf('?') === -1 ? baseURL + '?' : baseURL;

  return request('GET', url + listParamsToQueryString(params), undefined, options);
}

export function listParamsToQueryString(params: { [key: string]: Object }) {
  let queryString = '';
  let add = (key: string, value: {}) => {
    if (value !== null && value !== undefined && value !== '') {
      queryString += '&' + key + '=' + encodeURIComponent(value.toString());
    }
  };

  for (let key in params) {
    if (!params.hasOwnProperty(key)) {
      continue;
    }

    let value = params[key];
    if (value instanceof Array) {
      for (let item of value) {
        add(key, item);
      }
    } else {
      add(key, value);
    }
  }

  return queryString;
}

export function injectTenantInURL(url: string, version: number = 1) {
  let tenant = session.tenant();
  let prefix = version === 1 ? '/api/' : '/api/v2/';
  const tenantPart = version === 1 ? 't/' : 'tenants/';

  if (tenant && url.indexOf(prefix) === 0) {
    return prefix + tenantPart + tenant.slug + '/' + url.substr(prefix.length);
  } else {
    return url;
  }
}

export function uploadFile<T>(
  url: string,
  data: ArrayBuffer | FormData | string,
  options: { headers?: { [key: string]: string | null }; xhr?: () => XMLHttpRequest } = {}
): Promise<T> {
  return session.getToken().then(
    (token) =>
      new Promise<T>((resolve, reject) => {
        $.ajax(injectTenantInURL(url), {
          method: 'PUT',
          data,
          processData: !(data instanceof ArrayBuffer) && !(data instanceof FormData),
          contentType:
            data instanceof ArrayBuffer ? undefined : data instanceof FormData ? false : 'application/json',
          headers: {
            Authorization: token,
            'X-User-Language': localStorage.getItem('userLanguage'),
            ...options.headers,
          },
          dataType: 'json',
          ...(options.xhr ? { xhr: options.xhr } : {}),
          success: (res: T) => resolve(res),
          error: (xhr) => reject(xhr),
        });
      })
  );
}

export interface GetOptions {
  tenantId?: string;
  publicList?: boolean;
  getParams?: {};
}

export function makeList<TRet, TParam = {}>(endpointSegment: string) {
  return (
    params: ListRequestParams & TParam,
    options?: GetOptions,
    requestOptions?: RequestOptions
  ): Promise<TRet[]> => {
    if (options && options.tenantId && options.publicList) {
      return listRequest('/api/t/' + options.tenantId + '/' + endpointSegment + '/list_public/', params, {
        injectTenant: false,
        skipToken: options.publicList,
        ...requestOptions,
      });
    } else if (options && options.tenantId) {
      return listRequest('/api/t/' + options.tenantId + '/' + endpointSegment + '/', params, {
        injectTenant: false,
        ...requestOptions,
      });
    } else {
      return listRequest('/api/' + endpointSegment + '/', params, {
        injectTenant: true,
        ...requestOptions,
      });
    }
  };
}

export function makeRetrieve<T>(endpointSegment: string) {
  return (id: string, options?: GetOptions): Promise<T> => {
    if (options && options.tenantId && options.publicList) {
      return request(
        'GET',
        '/api/t/' + options.tenantId + '/' + endpointSegment + '/' + id + '/public/',
        undefined,
        { injectTenant: false }
      );
    } else if (options && options.tenantId) {
      return request(
        'GET',
        '/api/t/' + options.tenantId + '/' + endpointSegment + '/' + id + '/',
        undefined,
        { injectTenant: false }
      );
    } else {
      let url = `/api/${endpointSegment}/${id}/`;
      if (options?.getParams) {
        url += '?' + new URLSearchParams(options.getParams).toString();
      }
      return request('GET', url);
    }
  };
}

export function makeSave<T extends { id?: string }>(endpointSegment: string) {
  return (data: T, options?: GetOptions): Promise<ValidationResult> => {
    let endpoint = '/api/' + endpointSegment + '/';
    let method = 'POST';
    if (data.id) {
      endpoint += data.id + '/';
      method = 'PUT';
    }
    if (options?.getParams) {
      endpoint += '?' + new URLSearchParams(options.getParams).toString();
    }
    return requestWithValidation(method, endpoint, data);
  };
}

export function makeRemove(endpointSegment: string) {
  return (id: string): Promise<RemoveResult> =>
    requestRemoveEntity('/api/' + endpointSegment + '/' + id + '/');
}

export interface API<T, TParam = {}, TDetail = T> {
  list(
    params: ListRequestParams & TParam & { type?: string },
    options?: GetOptions,
    requestOptions?: RequestOptions
  ): Promise<T[]>;
  retrieve(id: string, options?: GetOptions): Promise<TDetail>;
  save(data: TDetail, options?: GetOptions): Promise<ValidationResult>;
  remove(id: string): Promise<RemoveResult>;
}

export function makeDefaultApi<T extends { id?: string }, TParam = {}, TDetail = T>(
  endpointSegment: string
): API<T, TParam, TDetail> {
  return {
    list: makeList<T, TParam>(endpointSegment),
    retrieve: makeRetrieve<TDetail>(endpointSegment),
    save: makeSave<TDetail>(endpointSegment),
    remove: makeRemove(endpointSegment),
  };
}

/** The range a response covers, in the format "items start-end/total". */
export interface ContentRange {
  start: number;
  end: number;
  total: number | null;
}

function findContentRangeHeader(headers: string): string | null {
  return headers.split('\r\n').find((header) => header.toLowerCase().startsWith('content-range:')) ?? null;
}

function parseContentRange(contentRangeHeader: string): ContentRange | null {
  const match = /(?:items\s)?(\d+)-(\d+)\/(\d+|\*)/.exec(contentRangeHeader);
  if (!match) {
    return null;
  }

  const [, start, end, total] = match;
  return {
    start: Number(start),
    end: Number(end),
    total: total === '*' ? null : parseInt(total, 10),
  };
}

/**
 * Return the range and the total a response covers.
 *
 * @throws {Error} If the response carries no Content-Range header, or one this cannot read.
 */
export function getContentRange(headers: string): ContentRange {
  const contentRangeHeader = findContentRangeHeader(headers);
  if (!contentRangeHeader) {
    throw new Error('Content-Range header not found');
  }

  const contentRange = parseContentRange(contentRangeHeader);
  if (!contentRange) {
    throw new Error('Content-Range header is malformed');
  }

  return contentRange;
}

/** The total a `Content-Range` header reports, or null when the response carries none. */
export function contentRangeTotal(headers: string): number | null {
  const contentRangeHeader = findContentRangeHeader(headers);
  return contentRangeHeader === null ? null : (parseContentRange(contentRangeHeader)?.total ?? null);
}
